'use strict';

export function uiViewAccessibilityDirective() {
  return {
    restrict: 'A',
    link(scope, element) {
      // This fixes the aria-hidden issue with UI Router views
      // by applying our accessibility fix to all ui-view elements

      // Apply accessibility fix to this element
      element.attr('accessibility-fix', '');

      // An alternative approach is to use inert attribute instead of
      // aria-hidden but we need to check for browser support first
      // eslint-disable-next-line no-undef
      const supportsInert = 'inert' in HTMLElement.prototype;

      // Process a UI View element to fix accessibility issues
      const processUiView = uiView => {
        // eslint-disable-next-line no-undef
        const $uiView = angular.element(uiView);

        // Apply accessibility fix directive
        $uiView.attr('accessibility-fix', '');

        // For browsers that support it, use inert instead of aria-hidden
        // This helps prevent focus from going into hidden views
        if(supportsInert) {
          // Set up a mutation observer to detect aria-hidden changes
          // eslint-disable-next-line no-undef
          const viewObserver = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
              if(mutation.type === 'attributes'
                  && mutation.attributeName === 'aria-hidden') {
                // When aria-hidden is set to true, also set inert
                if(uiView.getAttribute('aria-hidden') === 'true') {
                  uiView.setAttribute('inert', '');
                } else {
                  uiView.removeAttribute('inert');
                }
              }
            });
          });

          viewObserver.observe(uiView, {
            attributes: true,
            attributeFilter: ['aria-hidden']
          });

          // Store observer for cleanup
          $uiView.data('inertObserver', viewObserver);

          // Initial state
          if(uiView.getAttribute('aria-hidden') === 'true') {
            uiView.setAttribute('inert', '');
          }
        }
      };

      // Process all existing UI Views
      // eslint-disable-next-line no-undef
      const uiViews = document.querySelectorAll('[ui-view]');
      uiViews.forEach(processUiView);

      // Also add a mutation observer to detect dynamically created ui-views
      // eslint-disable-next-line no-undef
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if(mutation.type === 'childList') {
            mutation.addedNodes.forEach(node => {
              if(node.nodeType === 1) { // Element node
                // Check if the node itself is a ui-view
                if(node.hasAttribute('ui-view')) {
                  processUiView(node);
                }

                // Also check children
                uiViews.forEach(processUiView);
              }
            });
          }
        });
      });

      // eslint-disable-next-line no-undef
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      scope.$on('$destroy', () => {
        observer.disconnect();

        // Clean up any view-specific observers
        if(supportsInert) {
          uiViews.forEach(uiView => {
            // eslint-disable-next-line no-undef
            const $uiView = angular.element(uiView);
            const viewObserver = $uiView.data('inertObserver');
            if(viewObserver) {
              viewObserver.disconnect();
            }
          });
        }
      });
    }
  };
}

uiViewAccessibilityDirective.$inject = [];
